/* import __COLOCATED_TEMPLATE__ from './edit-sh-invoice-transaction-modal.hbs'; */
import { A } from '@ember/array';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import LSGModalBase from "../lsg/lsg-modal-dialog-base";
import { SH_INVOICE } from "../../../models/sh-invoice";
import { task } from 'ember-concurrency';
import { makeMoney } from 'sh-www/helpers/make-money';
import { closeModal } from 'sh-www/utils/modal-controls';

const NONE_OPTION_DEFAULT_TEXT = 'None';

export default LSGModalBase.extend({
  /**
   * Register services
   */
  companyCreditCommandService: service('commands/company-credit-commands'),
  store: service(),
  shData: service(),
  shInvoiceCommandService : service('commands/sh-invoice-commands'),
  shInvoiceTransactionCommandService: service('commands/sh-invoice-transaction-commands'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /** @type {Boolean} */
    markFullyPaid: false,

    /**
     * Pass in this as NULL will trigger the add experience.
     *
     * @param {ShInvoiceTransaction}      Optional.
     */
    shInvoiceTransaction: null,

    /**
     * @param {ShInvoice}     Required
     */
    shInvoice: null,

    /**
     * @param {Function}     Called after we have successfully edited a transaction
     */
    onTransactionEdited: () => {},

    /**
     * @param {Array<CompanyCredit>}
     */
    companyCredits: A([]),
  },

  /**
   * @inheritDoc
   */
  stateDefaults: {
    /**
     * @type {String}
     */
    errorText: '',

    /**
     * @type {Boolean}      True when we are updating our state with db
     */
    isUpdating: false,

    /**
     * @type {CostCode}
     */
    selectedCompanyCreditCostCode: null,

    /**
     * @type {Number}
     */
    updatedAmount: '',

    /**
     * @type {String}       of type SH_INVOICE.PROCESSORS.*
     */
    updatedProcessorType: SH_INVOICE.PROCESSORS.INVOICE,

    /**
     * @type {String}
     */
    updatedProcessorAccountId: '',

    /**
     * @type {String}
     */
    updatedProcessorTransactionId: '',
  },

  /**
   * @type {Boolean}      True if we are in the add mode vs edit
   */
  _isAddingNewTransaction: computed('model.shInvoiceTransaction.id', function() {
    return !this.get('model.shInvoiceTransaction.id');
  }),

  /**
   * @inheritDoc
   */
  _onInitializationComplete() {
    let shInvoiceTransaction = this.get('model.shInvoiceTransaction');

    if (shInvoiceTransaction) {
      this.set('state.updatedAmount', shInvoiceTransaction.get('amount'));
      this.set('state.updatedProcessorType', shInvoiceTransaction.get('processor'));
      this.set('state.updatedProcessorAccountId', shInvoiceTransaction.get('processorAccountId'));
      this.set('state.updatedProcessorTransactionId', shInvoiceTransaction.get('processorTransactionId'));
    }

    this.get('loadCompanyCreditsTask').perform();
  },

  loadCompanyCreditsTask: task(function *() {
    let response;
    try {
      response = yield this.get('companyCreditCommandService').loadCompanyCredits({
        companyId: this.get('model.shInvoice.company.id'),
        includes: ['sh-general-invoice'],
      });
    } catch (error) {
      return A([]);
    }

    this.shData.pushResponseToStore(response);

    this.set('model.companyCredits', this.shData.getModelArrayFromResponse(response, 'companyCredit'));
  }).drop(),

  companyCreditCostCodeAmounts: computed(
    'model.companyCredits.[]',
    function() {
      return this.get('model.companyCredits').reduce((accum, companyCredit) => {
        const costCodeId = companyCredit.get('costCode.id');
        if (!accum[costCodeId]) {
          accum[costCodeId] = {
            name: companyCredit.get('costCode.name'),
            amount: 0,
          };
        }

        if (companyCredit.isReleased) {
          accum[costCodeId].amount += companyCredit.amountRemainingInCents;
        }

        return accum;
      }, { });
    }
  ),

  costCodeIdToCostCodeMap: computed(
    'model.companyCredits.[]',
    function() {
      return this.model.companyCredits.reduce((accum, companyCredit) => {
        const { costCode } = companyCredit;
        accum[costCode.id] = costCode;

        return accum;
      }, { });
    }
  ),

  companyCreditSelectedValue: computed(
    'companyCreditSelectOptions.@each.value',
    'state.selectedCompanyCreditCostCode',
    function() {
      const selection = this.companyCreditSelectOptions.find(({ value }) => value === this.state.selectedCompanyCreditCostCode);
      return selection?.key ?? NONE_OPTION_DEFAULT_TEXT;
    }
  ),

  /**
   * options for the select for the processor type
   *
   * @type {Ember.A<Object>}
   */
  companyCreditSelectOptions: computed(
    'companyCreditCostCodeAmounts.[]',
    function() {
      const { companyCreditCostCodeAmounts } = this;
      const options = [
        {
          key: NONE_OPTION_DEFAULT_TEXT,
          value: null,
        },
      ];

      return Object.keys(companyCreditCostCodeAmounts).reduce((accum, costCodeId) => {
        const value = companyCreditCostCodeAmounts[costCodeId];
        const amountInDollars = makeMoney([(value.amount / 100).toFixed(2)]);

        accum.push({
          key: `${value.name} - ${amountInDollars}`,
          value: costCodeId,
        });

        return accum;
      }, options);
    }
  ),

  /**
   * options for the select for the processor type
   *
   * @type {Array<Object>}
   */
  _processorTypeSelectOptions: computed('state.updatedProcessorType', function() {
    return Object.values(SH_INVOICE.PROCESSORS).reduce((accum, processorType) => {
      if (!this._isAddingNewTransaction && processorType === SH_INVOICE.PROCESSORS.CREDIT) {
        return accum;
      }

      accum.push({
        key: processorType,
        value: processorType,
      });

      return accum;
    }, []);
  }),

  /**
   * @type {boolean}
   */
  showCompanyCreditSelector: computed(
    'state.updatedProcessorType',
    function() {
      return this.get('state.updatedProcessorType') === SH_INVOICE.PROCESSORS.CREDIT;
    }
  ),

  actions: {
    handleClose() {
      closeModal(this);
    },

    /**
     * @param {Object} option from the dropdown
     */
    onCompanyCreditSelected({ value }) {
      this.set('state.selectedCompanyCreditCostCode', value);
    },

    /**
     * @param {Object} option from the dropdown
     */
    onProcessorTypeSelected({ value }) {
      this.set('state.updatedProcessorType', value);
    },

    /**
     *
     */
    onUpdateLineItemClicked: function() {
      const markFullyPaid = this.get('model.markFullyPaid');
      const amount = markFullyPaid
        ? this.model.shInvoice.unpaidAmount
        : this.state.updatedAmount;

      const processorType = this.get('state.updatedProcessorType');
      const processorAccountId = this.get('state.updatedProcessorAccountId');
      const processorTransactionId = this.get('state.updatedProcessorTransactionId');
      const creditCostCodeID = this.state.selectedCompanyCreditCostCode;

      const creditCostCode = this.costCodeIdToCostCodeMap[creditCostCodeID];

      if (!amount && !markFullyPaid) {
        this.set('state.errorText', 'All must be filled in');
        return;
      }

      if (
        !creditCostCode
        && ( !processorType || !processorAccountId || !processorTransactionId)
      ) {
        this.set('state.errorText', 'All must be filled in');
        return;
      }

      this.set('state.errorText', '');
      this.set('state.isUpdating', true);

      if (this.get('_isAddingNewTransaction')) {
        this.get('shInvoiceTransactionCommandService').addTransaction({
          shInvoice: this.get('model.shInvoice'),
          amount,
          processorType,
          processorAccountId,
          processorTransactionId,
          creditCostCodeId: creditCostCode?.id,
          integrityChecksum: creditCostCode?.integrityChecksum,
        }).then(() => {
          closeModal(this);
          return this.get('model.onTransactionEdited')();
        }).finally(() => this.set('state.isUpdating', false));
      } else {
        this.get('shInvoiceTransactionCommandService').editTransaction(
          this.get('model.shInvoiceTransaction'),
          amount,
          processorType,
          processorAccountId,
          processorTransactionId
        ).then(() => {
          closeModal(this);
          this.get('model.onTransactionEdited')();
        }).catch(() => {
          // gobble gobble
        }).finally(() => this.set('state.isUpdating', false));
      }
    },

    onInputProcessorAccountId(accountId) {
      this.set('state.updatedProcessorAccountId', accountId);
    },

    onInputTransactionAmount(amount) {
      this.set('state.updatedAmount', amount);
    },

    onInputProcessorTransactionId(transactionId) {
      this.set('state.updatedProcessorTransactionId', transactionId);
    },

    closeModal() {
      closeModal(this);
    },
  }
});
